/**
 * Navbar Component
 * Main navigation bar with responsive mobile menu
 * Displays navigation links, authentication controls, and user info
 */

import { Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { SignInButton } from '../auth/SignInButton';
import { SignOutButton } from '../auth/SignOutButton';
import { UserInfo } from '../auth/UserInfo';
import { CreditDisplay } from '../auth/CreditDisplay';
import { ThemeToggle } from './ThemeToggle';

/**
 * Navbar component
 * Provides site navigation with responsive hamburger menu for mobile
 * Shows authentication status and user credits when logged in
 * 
 * @returns Navigation bar component
 */
export function Navbar() {
  const { isAuthenticated, user } = useAuthContext();

  // Check if user is admin (you can adjust this logic based on your auth setup)
  const isAdmin = user?.email?.includes('admin') || false;

  return (
    <nav 
      className="navbar-container"
      style={{
        width: '100%',
        background: 'var(--color-surface)',
        color: 'var(--color-fg)',
        fontFamily: 'var(--font-family)',
        fontSize: 'var(--font-size-xs)',
        borderBottom: '4px solid var(--color-accent)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        marginBottom: '1.25rem'
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
        <div className="navbar-content">
          {/* Logo */}
          <Link 
            to="/" 
            className="navbar-logo"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.25rem 0',
              textDecoration: 'none'
            }}
          >
            <img 
              src="https://s3.us-west-2.amazonaws.com/www.8bitcomposer.com/logo.png" 
              alt="8-Bit Composer Logo" 
              style={{ height: '2rem', width: '2rem' }}
            />
            <span style={{ color: 'var(--color-accent)', fontSize: 'var(--font-size-sm)' }}>
              8-Bit Composer
            </span>
          </Link>
          
          {/* Navigation links */}
          <div className="navbar-links">

          {isAdmin && (
            <Link
              to="/admin"
              style={{
                border: '2px solid var(--color-accent)',
                padding: '0.5rem 1rem',
                fontSize: 'var(--font-size-xs)',
                borderRadius: '4px',
                textDecoration: 'none',
                color: 'var(--color-fg)',
                transition: 'all 0.2s'
              }}
            >
              Admin 📊
            </Link>
          )}
          <Link 
            to="/"
            style={{
              color: 'var(--color-fg)',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
          >
            Home
          </Link>
          <Link 
            to="/about"
            style={{
              color: 'var(--color-fg)',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
          >
            About
          </Link>
          <Link 
            to="/contact"
            style={{
              color: 'var(--color-fg)',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
          >
            Contact
          </Link>
          <ThemeToggle />
          <SignInButton />
          <SignOutButton />
          </div>
        </div>
      </div>
      
      {/* User info and credits section */}
      {isAuthenticated && (
        <div style={{
          textAlign: 'center',
          color: 'var(--color-accent)',
          fontSize: 'var(--font-size-xs)',
          marginTop: '0.25rem',
          paddingBottom: '0.5rem'
        }}>
          {/* Welcome message */}
          <div style={{ marginBottom: '0.5rem' }}>
            <UserInfo />
          </div>
          
          {/* Credits display - centered under welcome message */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
            <CreditDisplay />
          </div>
        </div>
      )}
    </nav>
  );
}
